
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface RelacionamentoComDetalhes {
  id: string;
  solicitacao_id: string;
  relacionada_id: string;
  tipo_relacao: 'depende_de' | 'dependente';
  criado_em: string;
  numero: string;
  titulo: string;
  sla_nome: string | null;
  atendente_nome: string | null;
  // ID da solicitação exibida na linha (pode ser inverso)
  display_solicitacao_id: string;
}

const invertTipo = (tipo: 'depende_de' | 'dependente'): 'depende_de' | 'dependente' =>
  tipo === 'depende_de' ? 'dependente' : 'depende_de';

export const useRegistrosRelacionados = (solicitacaoId: string) => {
  const queryClient = useQueryClient();

  const { data: relacionamentos = [], isLoading } = useQuery({
    queryKey: ['registros-relacionados', solicitacaoId],
    queryFn: async () => {
      // Fetch both directions in parallel
      const [directRes, inverseRes] = await Promise.all([
        supabase
          .from('solicitacao_relacionamentos')
          .select('id, solicitacao_id, relacionada_id, tipo_relacao, criado_em')
          .eq('solicitacao_id', solicitacaoId),
        supabase
          .from('solicitacao_relacionamentos')
          .select('id, solicitacao_id, relacionada_id, tipo_relacao, criado_em')
          .eq('relacionada_id', solicitacaoId),
      ]);

      if (directRes.error) throw directRes.error;
      if (inverseRes.error) throw inverseRes.error;

      const directData = directRes.data || [];
      const inverseData = inverseRes.data || [];

      // Collect all IDs we need to fetch details for
      const targetIds = new Set<string>();
      directData.forEach(r => targetIds.add(r.relacionada_id));
      inverseData.forEach(r => targetIds.add(r.solicitacao_id));

      if (targetIds.size === 0) return [];

      const { data: solicitacoes, error: solError } = await supabase
        .from('solicitacoes')
        .select(`
          id, numero, titulo,
          sla:slas(nome),
          atendente:users!solicitacoes_atendente_id_fkey(name)
        `)
        .in('id', Array.from(targetIds));

      if (solError) throw solError;

      const solMap = new Map((solicitacoes || []).map(s => [s.id, s]));

      const mapSol = (id: string) => {
        const sol = solMap.get(id);
        const slaData = sol?.sla;
        const atendenteData = sol?.atendente;
        return {
          numero: sol?.numero || '',
          titulo: sol?.titulo || '',
          sla_nome: slaData && typeof slaData === 'object' && 'nome' in slaData ? (slaData as any).nome : null,
          atendente_nome: atendenteData && typeof atendenteData === 'object' && 'name' in atendenteData ? (atendenteData as any).name : null,
        };
      };

      const results: RelacionamentoComDetalhes[] = [];

      // Direct records: display relacionada_id
      directData.forEach(r => {
        results.push({
          ...r,
          ...mapSol(r.relacionada_id),
          tipo_relacao: r.tipo_relacao as 'depende_de' | 'dependente',
          display_solicitacao_id: r.relacionada_id,
        });
      });

      // Inverse records: display solicitacao_id, invert tipo
      inverseData.forEach(r => {
        results.push({
          ...r,
          ...mapSol(r.solicitacao_id),
          tipo_relacao: invertTipo(r.tipo_relacao as 'depende_de' | 'dependente'),
          display_solicitacao_id: r.solicitacao_id,
        });
      });

      return results;
    },
    enabled: !!solicitacaoId,
  });

  const addRelacionamento = useMutation({
    mutationFn: async ({ relacionadaId, tipoRelacao }: { relacionadaId: string; tipoRelacao: 'depende_de' | 'dependente' }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('solicitacao_relacionamentos')
        .insert({
          solicitacao_id: solicitacaoId,
          relacionada_id: relacionadaId,
          tipo_relacao: tipoRelacao,
          criado_por: userData.user?.id,
        });
      if (error) throw error;
      return { relacionadaId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['registros-relacionados', solicitacaoId] });
      queryClient.invalidateQueries({ queryKey: ['registros-relacionados', data.relacionadaId] });
      toast.success('Relacionamento adicionado');
    },
    onError: (error: any) => {
      if (error?.code === '23505') {
        toast.error('Este relacionamento já existe');
      } else {
        toast.error('Erro ao adicionar relacionamento');
      }
    },
  });

  const removeRelacionamento = useMutation({
    mutationFn: async ({ id, otherSolicitacaoId }: { id: string; otherSolicitacaoId: string }) => {
      const { error } = await supabase
        .from('solicitacao_relacionamentos')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { otherSolicitacaoId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['registros-relacionados', solicitacaoId] });
      queryClient.invalidateQueries({ queryKey: ['registros-relacionados', data.otherSolicitacaoId] });
      toast.success('Relacionamento removido');
    },
    onError: () => {
      toast.error('Erro ao remover relacionamento');
    },
  });

  const dependentes = relacionamentos.filter(r => r.tipo_relacao === 'dependente');
  const dependeDe = relacionamentos.filter(r => r.tipo_relacao === 'depende_de');

  return {
    dependentes,
    dependeDe,
    isLoading,
    addRelacionamento,
    removeRelacionamento,
  };
};
